import { withListOverflow } from "../components/ListOverflow.jsx";
export const LeaseDetailBody = withListOverflow(LeaseDetailListOwner);
// src/v2/leases/LeaseDetailBody.jsx
//
// LEASES-DETAIL-PAGE-BLANK — full-detail body rendered on /leases/:leaseId.
// LEASE-DETAIL-TABS-SHELL — split into 5 tab panes (Overview · Terms · Financials · Dates · Intel).
// LEASE-DETAIL-V5-S1 — 6th Documents tab added (after Dates, before Intel);
//   Documents section removed from Terms pane; onDocumentsCount callback lifts
//   the count to LeaseDetailPage for the tab badge.
// LEASE-DETAIL-V5-S2 — Overview + Terms restructured into CSS grids.
//   Overview order: HeroBar → Timeline → ProvenanceBar → LeasesAtLocationCard → lower section.
//   Overview lower section: 2-col grid (left: Key terms + Parties stacked | right: FinancialsAtAGlance)
//     followed by full-width DecisionsAhead(list) row.
//   Terms 2×2: LeaseSum(key-terms) | OpEx | Options&Rights | Restrictions&Restoration — all rendered open.
//   onTabChange prop added so FinancialsAtAGlanceCard can switch to the Financials tab.
// LEASES-OVERVIEW-REBALANCE — Party fields (Lessor/Lessee/Contact) removed from Key terms;
//   Lessor Contact in Parties panel split into Name + Contact type discrete rows;
//   columns widened to prevent value wrapping; Decisions Ahead moved to full-width row.
// LEASES-OVERVIEW-REDESIGN — Overview tab rebuilt as a 2-column × 3-row grid:
//   Row 1: LocationPropertyPane (photo/map/address) | RentAndTermPane (KPIs + timeline + currency toggle)
//   Row 2: LeaseSummaryCard(summary) | FinancialsAtAGlanceCard
//   Row 3: LeaseSummaryCard(parties) | DatesAheadPane (expiry + renewal decisions, year countdowns)
//   Removed standalone rows: LeaseTabHeroBar, LeaseTimeline, RecordProvenanceBar,
//     LeasesAtLocationCard, DecisionsAheadCallout.
//   Added: getLocation() fetch for linked location data; provenance legend below grid.
// LEASE-DETAIL-V5-S3 — Financials tab restructured:
//   50/50 top row: FinancialTermsCard (left) | right column stack (CostTrajectoryPanel + KeyRatiosPanel).
//   Concessions split into two labelled subsections: Allowances (drawable) and Abatements.
//
// Fetches its own data (criticalDates, allowances, documents, lease list, location)
// and composes the same panels that LeaseSummaryTab rendered before LEASES-FORKLIFT-2.
// Content is partitioned across 6 tabs using display:none keep-alive so stateful
// panes (Intel's own fetch) are not torn down on every tab switch.
//
// Tab panes:
//   Overview   — 2×3 grid (LocationPropertyPane | RentAndTermPane(KPIs+Timeline+provenance) |
//                           LeaseSummaryCard(summary) | FinancialsAtAGlanceCard |
//                           LeaseSummaryCard(parties) | DatesAheadPane) · provenance legend
//   Terms      — 2×2 grid (LeaseSummaryCard | OpEx pane | Options&Rights pane | Restrictions pane)
//   Financials — 50/50 grid (FinancialTermsCard | CostTrajectoryPanel+KeyRatiosPanel stack)
//                · RentScheduleCard · Concessions (Allowances + Abatements subsections)
//   Dates      — Critical Dates ActivitySection
//   Documents  — LeaseDocumentsTab (read-only register)
//   Intel      — IntelTab (locationId)
//
// Props:
//   leaseId          — from URL (:leaseId); pins which lease is displayed
//   locationId       — from parent header fetch (lease.locationId)
//   currentRole      — "viewer" | "editor" | "admin"
//   activeTab        — "overview" | "terms" | "financials" | "dates" | "documents" | "intel"
//   onDocumentsCount — (count: number) => void  lifted to LeaseDetailPage for tab badge
//   onTabChange      — (tab: string) => void  from LeaseDetailPage; used by FinancialsAtAGlanceCard

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { useFxRates } from "../../data/useFxRates.js";
import {
  getActiveLeaseSummary,
  listLocationLeases,
  listCriticalDates,
  listAllowances,
  listDocuments,
  listLeaseRecoveries,
} from "../../data/leasesClient.js";
import { log } from "../../utils/logger.js";
import { getLocation }               from "../../data/locationsClient.js";

import { LeaseTimeline }             from "../../features/leases/components/LeaseTimeline.jsx";
import { LeaseSummaryCard }          from "../../features/leases/components/LeaseSummaryCard.jsx";
import { FinancialsAtAGlanceCard }   from "../../features/leases/components/FinancialsAtAGlanceCard.jsx";
import { FinancialTermsCard }        from "../../features/leases/components/FinancialTermsCard.jsx";
import { RentScheduleCard }          from "../../features/leases/components/RentScheduleCard.jsx";
import { ConcessionCard, calcRemaining, isExpired } from "../../features/leases/components/ConcessionCard.jsx";
import { EditableLeaseField }        from "../../features/leases/components/EditableLeaseField.jsx";
import { SpacebaseBanner }           from "../../features/leases/components/SpacebaseBanner.jsx";
import { CurrencyToggle }            from "../../features/leases/components/CurrencyToggle.jsx";
import { PaneInfoTooltip } from "../pulse/PaneInfoTooltip.jsx";
import { useFxConversionBasis, formatFxBasisSummary } from "../../data/useFxConversionBasis.js";
import { fmtMoney, fmtMoneyCompact, getCurrencySymbol, formatFootprintSqftFull as formatSqftFull } from "../../utils/format.js";
import { leaseTypeLabel } from "../../features/leases/leaseTypes.js";
import { convertAmount }             from "../../utils/fxConvert.js";
import { EditLeaseModal }            from "../../features/leases/EditLeaseModal.jsx";
import { ActivitySection }           from "../../features/leases/components/ActivitySection.jsx";
import { CriticalDateRow }           from "../../features/leases/components/CriticalDateRow.jsx";
import { LeaseDatesPane }            from "./LeaseDatesPane.jsx";
import { IntelTab }                  from "../../features/locations/IntelTab.jsx";
import { LeaseDocumentsTab }         from "./LeaseDocumentsTab.jsx";
import { LocationPropertyPane }      from "../../features/locations/LocationPropertyPane.jsx";
import { buildLeaseUrl }             from "../../services/buildUrl.js";
import { buildDecisionsItems, sourceFor } from "../../features/leases/LeaseSummaryTab.jsx";
import { ProvenanceBadge } from "../../features/leases/components/ProvenanceBadge.jsx";
import { buildRentSchedule }         from "../../features/leases/selectors/rentSchedule.js";
import { selectLeaseDetail } from "../../shared/selectLeaseDetail.js";
import { leaseAnnualHeadlines } from "../../shared/leaseAnnualHeadlines.js";
import { monthlyPayableEvidence } from "../../../shared/monthlyPayable.js";
import { currentLeaseFootprint } from "../../../shared/leaseAreaBasis.js";
import { leaseAreaPresentation } from "../../features/leases/leaseAreaPresentation.js";
import { leaseAreaSummary }          from "../../features/leases/leaseAreaSummary.js";
import {
  ACCORDION_PANELS,
  parseDateOnly,
  isSentinelEndDate,
}                                    from "../../features/leases/leaseTabConfig.js";
import { formatLongExpiryDate, isActionablePastDueExpiry, isPastExpiryDate } from "../../features/leases/expiryDisplay.js";
import { calculateSubleaseRecovery } from "../../features/leases/selectors/subleaseRecovery.js";
import { AnnualRentBreakdown, hasRecoveryBasis, netRentDisplay } from "../../features/leases/components/AnnualRentBreakdown.jsx";
import { DrawerKeyTermsPane, DrawerPremisesPane, DrawerTermsPane } from "./LeaseDrawerV2Parts.jsx";
import { LeaseDrawerRentSchedule, RemainingObligationTile, LeaseDrawerTrajectory, LifetimeCoverageNote } from "./LeaseDrawerRentSchedule.jsx";
import { drawerMoney, sumDrawerMoney } from "./drawerMoney.js";

// ─── Display label map for accordion field rows ────────────────────────────

const FIELD_LABEL = {
  renewalCount:            "Renewal count",
  breakDate:               "Break option date",
  rofrRofoNotes:           "ROFR / ROFO",
  expansionOptionNotes:    "Expansion option",
  opexStructure:           "OpEx structure",
  baseYear:                "Base year",
  baseYearOpex:            "Base year OpEx",
  baseYearTax:             "Base year tax",
  opexCapPercent:          "OpEx cap %",
  securityDepositAmount:   "Security deposit",
  noticeAddress:           "Notice address",
  permittedUse:            "Permitted use",
  subletConsentRequired:   "Subletting consent",
  sqftMeasurementStandard: "SF measurement",
  subletRecaptureRight:    "Recapture right",
  restorationObligations:  "Restoration obligations",
  subletProfitSharePercent:"Subletting profit share %",
};

// ─── Pane visibility helper ───────────────────────────────────────────────────
// Returns a style object that shows/hides the pane while keeping it mounted.
// Intel has its own internal fetch; keep-alive prevents re-fetching on switch.
function paneStyle(isActive) {
  return isActive ? {} : { display: "none" };
}

function leaseSizeDisplay(lease) {
  const grossSf = currentLeaseFootprint(lease).grossSf;
  return grossSf != null ? formatSqftFull(grossSf) : null;
}

// ─── Shared 2×2 grid style ────────────────────────────────────────────────────
const GRID_2X2 = {
  display:             "grid",
  gridTemplateColumns: "1fr 1fr",
  gridTemplateRows:    "auto auto",
  gap:                 14,
  alignItems:          "stretch",
};

// ─── Custom render map for specific term fields ───────────────────────────────
// Provides JSX-returning render functions for fields that need non-default
// formatting in the TermsPaneCard (e.g. security deposit as currency).
// Each entry is (rawValue, lease) => ReactNode.
const FIELD_RENDER_MAP = {
  securityDepositAmount: (v, lease) => {
    const n = parseFloat(v);
    if (!isFinite(n)) return String(v);
    return (
      <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>
        {fmtMoney(n, lease.currency || "USD")}
      </span>
    );
  },
};

// ─── Terms pane group card ────────────────────────────────────────────────────
// Open card for one Terms 2×2 group (Operating expenses / Options & rights /
// Restrictions & restoration). Replaces the old flat TermsPaneCard.
//
// Behaviour:
//   • Header shows a count pill: "N of M" (green when all specified).
//   • Specified fields render as label/value/badge rows (EditableLeaseField).
//   • Unspecified fields are collapsed into a single muted "Not in this lease"
//     footer. "+ add" affordance appears when canWrite is true.
//   • A group with 0 specified fields renders a single muted empty-state card
//     (never N individual grey rows).
export function TermsPaneCard({ title, panelKey, fields, lease, locationId, leaseId, canWrite, onEditOpen, contentOnly = false, displayCcy, rates }) {
  const specifiedFields   = fields.filter((f) => lease[f] != null && lease[f] !== "");
  const unspecifiedFields = fields.filter((f) => lease[f] == null || lease[f] === "");

  const specCount  = specifiedFields.length;
  const totalCount = fields.length;
  const pillFull   = specCount === totalCount && totalCount > 0;
  const isEmpty    = specCount === 0;

  return (
    <section
      data-testid={`terms-pane-${panelKey}`}
      style={contentOnly ? {} : {
        background:   T.card,
        border:       `1px solid ${T.border}`,
        borderRadius: 6,
        padding:      "11px 13px",
      }}
    >
      {/* Header with blue bar accent + count pill */}
      {!contentOnly && <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 3, height: 14, borderRadius: 2, background: T.blue, flexShrink: 0 }} />
        <span style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, flex: 1 }}>
          {title}
        </span>
        <span
          data-testid={`terms-count-pill-${panelKey}`}
          style={{
            fontSize:     T.fsXxs,
            fontWeight:   T.fwBold,
            color:        pillFull ? T.forestGreen : T.light,
            background:   pillFull ? T.pillGreenBg : T.tint,
            borderRadius: 999,
            padding:      "2px 9px",
          }}
        >
          {specCount} of {totalCount}
        </span>
      </div>}

      {isEmpty ? (
        // Empty-state: single muted dashed card with field list + add affordance.
        <div
          data-testid={`terms-empty-${panelKey}`}
          style={{
            fontSize:     T.fsSm,
            color:        T.light,
            fontWeight:   T.fwSemibold,
            background:   T.tint,
            border:       `1px dashed ${T.border}`,
            borderRadius: 8,
            padding:      14,
            textAlign:    "center",
            lineHeight:   1.5,
          }}
        >
          No {title.toLowerCase()} terms in this lease.
          <br />
          <span style={{ color: T.mid }}>
            {fields.map((f) => FIELD_LABEL[f] ?? f).join(", ")}
          </span>
          {canWrite && (
            <button
              type="button"
              data-testid={`terms-add-btn-${panelKey}`}
              onClick={() => onEditOpen?.(fields[0])}
              style={{
                display:    "block",
                marginTop:  6,
                background: "none",
                border:     "none",
                padding:    0,
                cursor:     "pointer",
                color:      T.blue,
                fontWeight: T.fwBold,
                fontSize:   T.fsSm,
              }}
            >
              + add
            </button>
          )}
        </div>
      ) : (
        <>
          {specifiedFields.map((field) => (
            <EditableLeaseField
              key={field}
              locationId={locationId}
              leaseId={leaseId}
              lease={lease}
              field={field}
              label={FIELD_LABEL[field] ?? field}
              value={lease[field]}
              canWrite={false}
              source={sourceFor(lease, field)}
              render={
                contentOnly && ["securityDepositAmount", "baseYearOpex", "baseYearTax"].includes(field)
                  ? () => {
                    const from = lease.currency || "USD";
                    const money = drawerMoney(lease[field], from, displayCcy, rates);
                    return <span title={money.tooltip}>{money.text}</span>;
                  }
                  : FIELD_RENDER_MAP[field]
                  ? () => FIELD_RENDER_MAP[field](lease[field], lease)
                  : undefined
              }
            />
          ))}

          {unspecifiedFields.length > 0 && (
            <div
              data-testid={`terms-not-in-lease-${panelKey}`}
              style={{
                marginTop:    9,
                fontSize:     T.fsXs,
                color:        T.light,
                fontWeight:   T.fwSemibold,
                background:   T.tint,
                border:       `1px dashed ${T.border}`,
                borderRadius: 8,
                padding:      "8px 11px",
              }}
            >
              <span>
                Not in this lease: {unspecifiedFields.map((f) => FIELD_LABEL[f] ?? f).join(" · ")}
              </span>
              {canWrite && (
                <button
                  type="button"
                  data-testid={`terms-add-btn-${panelKey}`}
                  onClick={() => onEditOpen?.(unspecifiedFields[0])}
                  style={{
                    display:    "block",
                    marginTop:  6,
                    background: "none",
                    border:     "none",
                    padding:    0,
                    cursor:     "pointer",
                    color:      T.blue,
                    fontWeight: T.fwBold,
                    fontSize:   T.fsXs,
                  }}
                >
                  + add
                </button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}


// ─── Shared panel card shell (matches FinancialTermsCard border/radius/padding) ──
const PANEL_CARD = {
  background:   T.card,
  border:       `1px solid ${T.border}`,
  borderRadius: 6,
  padding:      "11px 13px",
};

// ─── CostTrajectoryPanel ─────────────────────────────────────────────────────
// Hero dual-series bar chart built from the rent schedule. Each year bar shows
// two stacked segments: a lower solid-blue segment for net rent paid and an
// upper green cap for abatement savings. Years with no abatement render as a
// single light-blue (contractual) bar. The current year is labelled "now".
// Partial final-year bars are naturally shorter because their contractual value
// is lower (buildRentSchedule already scales partial years correctly).
//
// Props:
//   schedule    — output of buildRentSchedule() (array of row objects)
//   currentYear — number, e.g. 2026
//   nativeCcy   — lease currency code (e.g. "USD")
//   displayCcy  — user-selected display currency; bars and tooltips convert to it
//   rates       — FX rate map from useFxRates

const CHART_H = 88; // bar area height in px

function CostTrajectoryPanel({ schedule, currentYear, nativeCcy, displayCcy, rates }) {
  const from   = nativeCcy || "USD";
  const effCcy = displayCcy || from;
  const ccySym = getCurrencySymbol(effCcy);
  const chartSchedule = (schedule || []).filter((row) => !row.isNotInSource);

  function convertBar(nativeAmt, currency = from) {
    if (!nativeAmt) return 0;
    if (currency === effCcy || !rates) return nativeAmt;
    const c = convertAmount(nativeAmt, currency, effCcy, rates);
    return c != null && Number.isFinite(c) ? c : nativeAmt;
  }

  if (chartSchedule.length === 0) {
    return (
      <section data-testid="cost-trajectory-panel" style={PANEL_CARD}>
        <PaneHeading style={{ marginBottom: 10 }}>Cost trajectory</PaneHeading>
        <div style={{ fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}>No schedule data.</div>
      </section>
    );
  }

  const contractualValues = chartSchedule.map((r) => convertBar(
    r.basis === "monthly_actual" ? r.net ?? 0 : r.contractual || 0, r.currency));
  const maxVal    = Math.max(...contractualValues, 1);

  return (
    <section data-testid="cost-trajectory-panel" style={PANEL_CARD}>
      <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 8 }}>
        <PaneHeading style={{ marginBottom: 0 }}>Cost trajectory</PaneHeading>
        <span style={{ fontSize: T.fsXxs, color: T.mid, marginLeft: 8 }}>
          {chartSchedule.some((row) => row.basis === "monthly_actual") ? `actual payable ${ccySym}/yr where sourced` : `contractual ${ccySym}/yr`}
        </span>
      </div>

      {/* Bar chart — extra top padding (18px) reserves space for the "now" label */}
      <div
        data-testid="cost-trajectory-bars"
        style={{
          display:     "flex",
          alignItems:  "flex-end",
          gap:         4,
          height:      CHART_H + 18,
          paddingTop:  18,
          boxSizing:   "content-box",
        }}
      >
        {chartSchedule.map((row, i) => {
          const isCurrent   = row.year === currentYear;
          const isPast      = row.year <= currentYear;
          const contractual = contractualValues[i];
          const abatement   = row.basis === "monthly_actual" ? 0 : convertBar(row.abatement || 0, row.currency);
          const hasAbat     = abatement > 0;

          // Heights are proportional to contractual (full bar = contractual/max)
          const barH  = maxVal > 0 ? Math.max(4, (contractual / maxVal) * CHART_H) : 4;
          const abatH = hasAbat ? Math.max(2, (abatement / Math.max(contractual, 1)) * barH) : 0;
          const netH  = Math.max(0, barH - abatH);
          // Incurred/current bars: solid blue; future bars: light blue
          const netBg = isPast ? T.blue : T.histfill;

          const tip = `${row.year}: ${ccySym}${Math.round(contractual).toLocaleString("en-US")} ${row.basis === "monthly_actual" ? "sourced actual payable (net of abatement)" : "contractual"}`
            + (hasAbat ? ` / ${ccySym}${Math.round(abatement).toLocaleString("en-US")} abated` : "");

          return (
            <div
              key={row.year}
              data-testid={`trajectory-bar-${row.year}`}
              data-native-amount={row.basis === "monthly_actual" ? row.net : row.contractual}
              title={tip}
              style={{
                flex:           1,
                display:        "flex",
                flexDirection:  "column",
                justifyContent: "flex-end",
                height:         "100%",
                position:       "relative",
              }}
            >
              {/* "now" label sits just above the rendered bar top */}
              {isCurrent && (
                <span
                  data-testid="trajectory-now-label"
                  style={{
                    position:  "absolute",
                    bottom:    barH + 2,
                    left:      "50%",
                    transform: "translateX(-50%)",
                    fontSize:  T.fsXxs,
                    fontWeight: T.fwBold,
                    color:     T.blue,
                    whiteSpace: "nowrap",
                    lineHeight: "14px",
                  }}
                >
                  now
                </span>
              )}

              {/* Green abatement cap (rendered first = sits on top) */}
              {hasAbat && (
                <div
                  data-testid="trajectory-abatement-cap"
                  style={{
                    height:       abatH,
                    background:   isPast ? T.ragGreenBorder : T.ragGreenBg,
                    border:       `1px solid ${T.ragGreenBorder}`,
                    borderBottom: "none",
                    borderRadius: "3px 3px 0 0",
                    flexShrink:   0,
                  }}
                />
              )}

              {/* Blue / light net segment */}
              <div
                style={{
                  height:       netH,
                  background:   netBg,
                  borderRadius: hasAbat ? 0 : "3px 3px 0 0",
                  flexShrink:   0,
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Axis labels — one centered label per bar */}
      <div style={{ display: "flex", marginTop: 4 }}>
        {chartSchedule.map((row) => (
          <div
            key={row.year}
            style={{
              flex:       1,
              textAlign:  "center",
              fontSize:   T.fsXxs,
              color:      row.year === currentYear ? T.blue : T.mid,
              fontWeight: row.year === currentYear ? T.fwBold : T.fwSemibold,
            }}
          >
            {row.year}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
        {[
          { bg: T.blue,           border: undefined,       label: "Net rent due · incurred" },
          { bg: T.histfill,       border: undefined,       label: "Net rent due · future" },
          ...(!chartSchedule.every((row) => row.basis === "monthly_actual")
            ? [{ bg: T.ragGreenBorder, border: T.ragGreenBorder, label: "Abatement savings - bar top = contractual (annual-source years)" }] : []),
        ].map(({ bg, border, label }) => (
          <span
            key={label}
            style={{ display: "flex", alignItems: "center", gap: 4, fontSize: T.fsXxs, color: T.mid }}
          >
            <span
              style={{
                width:        10,
                height:       10,
                borderRadius: 2,
                background:   bg,
                border:       border ? `1px solid ${border}` : `1px solid rgba(0,0,0,0.1)`,
                flexShrink:   0,
                display:      "inline-block",
              }}
            />
            {label}
          </span>
        ))}
      </div>
    </section>
  );
}

// ─── KeyRatiosPanel ──────────────────────────────────────────────────────────
// Three computed read-only rows:
//   Rent CAGR (term)      — annualised rate from schedule first to last contractual
//   Abatement remaining   — sum of rem across abatement-type allowances
//   Allowance unused      — sum of rem across drawable allowances with rem > 0
//
// Props:
//   schedule    — output of buildRentSchedule()
//   allowances  — raw allowances array
//   nativeCcy   — lease native currency code
//   displayCcy  — currently-selected display currency
//   rates       — FX rate map

function KeyRatiosPanel({ schedule, allowances, nativeCcy, displayCcy, rates, freeRentMonths, drawer = false }) {
  const effCcy = displayCcy || nativeCcy || "USD";
  const from   = nativeCcy || "USD";

  function toDisplay(nativeAmount) {
    if (nativeAmount == null || !Number.isFinite(nativeAmount)) return null;
    if (from === effCcy) return nativeAmount;
    const converted = convertAmount(nativeAmount, from, effCcy, rates);
    return converted != null ? converted : nativeAmount;
  }

  function fmt(nativeAmount) {
    const n = toDisplay(nativeAmount);
    return fmtMoney(n, n != null && from !== effCcy && convertAmount(1, from, effCcy, rates) != null ? effCcy : from);
  }

  // Rent CAGR: annualised from first to last schedule row by contractual value.
  let cagrStr = "—";
  const sourcedSchedule = (schedule || []).filter((row) => !row.isNotInSource);
  if (sourcedSchedule.length >= 2) {
    const first = sourcedSchedule[0].contractual;
    const last  = sourcedSchedule[sourcedSchedule.length - 1].contractual;
    const n     = sourcedSchedule.length - 1; // number of steps (years - 1)
    if (first > 0 && last > 0 && n > 0) {
      const cagr = Math.pow(last / first, 1 / n) - 1;
      cagrStr = `~${(cagr * 100).toFixed(1)}%`;
    }
  }

  // Abatement remaining: sum of rem for abatement-type allowances.
  let abatementRem = 0;
  let hasAbatement = false;
  for (const alw of (allowances || [])) {
    if (alw.type !== "abatement") continue;
    hasAbatement = true;
    const r = calcRemaining(alw.totalAmount, alw.drawnToDate);
    if (r != null && r > 0) abatementRem += r;
  }

  // Allowance unused: sum of rem for drawable (non-abatement), non-expired allowances with rem > 0.
  // Expired allowances have forfeited their remaining balance — exclude them (mirrors
  // FinancialsHeadlineStrip's allowanceLeft computation so both surfaces agree).
  let allowanceUnused = 0;
  let hasDrawable = false;
  for (const alw of (allowances || [])) {
    if (alw.type === "abatement") continue;
    if (isExpired(alw.expiryDate)) continue;
    hasDrawable = true;
    const r = calcRemaining(alw.totalAmount, alw.drawnToDate);
    if (r != null && r > 0) allowanceUnused += r;
  }

  const freeRentVal = freeRentMonths != null ? Number(freeRentMonths) : null;

  const rows = [
    {
      key:   "rent-cagr",
      label: "Rent CAGR (term)",
      value: cagrStr,
      isMoney: false,
    },
    {
      key:   "abatement-remaining",
      label: "Abatement remaining",
      // n/a: lease has no abatement clause — concept doesn't apply.
      value: hasAbatement ? drawer ? remainingAllowances(allowances, true, from, effCcy, rates) : fmt(abatementRem) : "n/a",
      isMoney: true,
    },
    {
      key:   "allowance-unused",
      label: "Allowance unused",
      // n/a: lease has no drawable allowance clause — concept doesn't apply.
      value: hasDrawable ? drawer ? remainingAllowances(allowances, false, from, effCcy, rates) : fmt(allowanceUnused) : "n/a",
      isMoney: true,
    },
    {
      key:   "free-rent-months",
      label: "Free rent",
      // n/a: lease has no free-rent period — concept doesn't apply.
      value: freeRentVal != null && freeRentVal > 0 ? `${freeRentVal} mo` : "n/a",
      isMoney: false,
    },
  ];

  return (
    <section data-testid="key-ratios-panel" style={{ ...PANEL_CARD, height: "100%", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
      <PaneHeading style={{ marginBottom: 10 }}>Key ratios</PaneHeading>
      <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        {rows.map((row, i) => (
          <div
            key={row.key}
            data-testid={`key-ratio-row-${row.key}`}
            style={{
              display:     "flex",
              alignItems:  "center",
              justifyContent: "space-between",
              padding:     "7px 0",
              borderTop:   i > 0 ? `1px solid ${T.border}` : undefined,
            }}
          >
            <span style={{ fontSize: T.fsBase, color: T.mid }}>{row.label}</span>
            <span style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}>
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── AbatementEntry ───────────────────────────────────────────────────────────
// Compact read-only row for abatement-type allowances. Abatements auto-apply
// and net into the rent schedule — they are never drawn in the cash-allowance
// sense, so the progress bar and draw history used by ConcessionCard are not
// appropriate here.
//
// Props:
//   allowance   — raw allowance row (type === "abatement")
//   displayCcy  — user-selected display currency
//   rates       — FX rate map

function AbatementEntry({ allowance, displayCcy, rates }) {
  const { label, totalAmount, currency, expiryDate } = allowance;
  const from   = currency || "USD";
  const effCcy = displayCcy || from;

  function fmtAmt(nativeAmt) {
    if (nativeAmt == null) return "—";
    const n = parseFloat(nativeAmt);
    if (!Number.isFinite(n)) return "—";
    if (from === effCcy || !rates) return fmtMoney(n, from);
    const c = convertAmount(n, from, effCcy, rates);
    return c != null && Number.isFinite(c) ? fmtMoney(c, effCcy) : fmtMoney(n, from);
  }

  const rem = calcRemaining(totalAmount, 0);

  return (
    <div
      data-testid="abatement-entry"
      style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        gap:            10,
        padding:        "8px 10px",
        background:     T.card,
        border:         `1px solid ${T.border}`,
        borderRadius:   5,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
        <span
          data-testid="concession-type-pill"
          style={{
            fontSize:    T.fsXxs,
            fontWeight:  T.fwBold,
            padding:     "1px 7px",
            borderRadius: 10,
            background:  T.ragAmberBg,
            color:       T.ragAmberText,
            border:      `1px solid ${T.ragAmberBorder}`,
            flexShrink:  0,
          }}
        >
          Abatement
        </span>
        <span style={{ fontSize: T.fsSm, color: T.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label || "Abatement"}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        {rem != null && (
          <span
            data-testid="abatement-total"
            style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark }}
          >
            {fmtAmt(rem)}
          </span>
        )}
        <span
          style={{
            fontSize:   T.fsXxs,
            color:      T.mid,
            fontStyle:  "italic",
            maxWidth:   180,
            textAlign:  "right",
          }}
        >
          Auto-applied · net into rent schedule
          {expiryDate ? ` · through ${expiryDate.slice(0, 7)}` : ""}
        </span>
      </div>
    </div>
  );
}

// ─── FinancialsHeadlineStrip ──────────────────────────────────────────────────
// 6-tile KPI strip at the top of the Financials pane.
//
// Tiles:
//   1. Annual rent   — net billed / contractual run-rate
//   2. Rate          — $/sf net / contractual
//   3. Total lifetime cost
//   4. Rent CAGR     — derived from schedule year-on-year contractual values
//   5. Abatement left — sum of remaining for abatement-type allowances
//   6. Allowance left — sum of remaining for non-abatement allowances

function HeadlineTile({ testId, label, primary, secondary, borderColor = T.border, primaryColor = T.dark }) {
  return (
    <div
      data-testid={testId}
      style={{
        background:   T.card,
        border:       `1px solid ${borderColor}`,
        borderRadius: 6,
        padding:      "10px 14px",
        display:      "flex",
        flexDirection:"column",
        gap:          3,
        minWidth:     0,
      }}
    >
      <div style={{
        fontSize:      T.fsXxs,
        fontWeight:    T.fwSemibold,
        color:         T.light,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}>
        {label}
      </div>
      <div style={{
        fontSize:   T.fsLg,
        fontWeight: T.fwBold,
        color:      primaryColor,
        lineHeight: 1.1,
      }}>
        {primary}
      </div>
      {secondary && (
        <div style={{ fontSize: T.fsXxs, color: T.mid }}>{secondary}</div>
      )}
    </div>
  );
}

function SubleaseRecoveryPanel({ lease, records, loading, error, rates, displayCcy }) {
  const nativeCurrency = String(lease?.currency || "USD").trim();
  const recordedHeadRent = lease?.annualRentNative != null && !lease?.isAnnualRentDerived
    ? Number(lease.annualRentNative)
    : (lease?.rentBasis === "fixed" && lease?.computed?.annualRentRunRate != null
      ? Number(lease.computed.annualRentRunRate)
      : null);
  const clientSummary = calculateSubleaseRecovery(records, recordedHeadRent, nativeCurrency, rates);
  // Server eligibility and FX own annual reconciliation. Keep the legacy
  // calculator only for older responses/fixtures without the annual contract.
  const summary = lease?.recoveryCoverage ? {
    headRentUsd: lease.grossAnnualRentUsd,
    incomeUsd: lease.recoveryCoverage.knownCount > 0 ? lease.subleaseRecoveryUsd : null,
    netExposureUsd: lease.annualRentUsd,
    recordCount: lease.recoveryCoverage.recordCount,
    recordedIncomeCount: lease.recoveryCoverage.knownCount,
    convertibleIncomeCount: lease.recoveryCoverage.knownCount,
    unconvertibleIncomeCount: lease.recoveryCoverage.missingFxCount,
    isPartial: lease.recoveryCoverage.partial,
  } : clientSummary;
  const incomeDisplay = summary.incomeUsd == null
    ? (summary.recordedIncomeCount > 0 ? "USD conversion unavailable" : "income not recorded")
    : drawerMoney(summary.incomeUsd, "USD", displayCcy || "USD", rates).text;
  const netDisplay = loading || error || summary.netExposureUsd == null
    ? "unavailable"
    : drawerMoney(summary.netExposureUsd, "USD", displayCcy || "USD", rates).text;
  const headRentDisplay = summary.headRentUsd == null
    ? "unavailable"
    : drawerMoney(summary.headRentUsd, "USD", displayCcy || "USD", rates).text;
  const incomeSub = summary.recordCount === 0
    ? "No recovery records"
    : (summary.unconvertibleIncomeCount > 0
      ? `${summary.convertibleIncomeCount} of ${summary.recordCount} records converted to USD`
      : `${summary.recordedIncomeCount} of ${summary.recordCount} records have income`);
  const netSub = summary.isPartial
    ? "Partial · excludes unrecorded or unconvertible income"
    : "Head rent less recorded sublease income";

  return (
    <section
      data-testid="sublease-recovery-panel"
      style={{
        ...PANEL_CARD,
        background: T.subleaseRecoveryBg,
        border: `1px solid ${T.subleaseRecoveryBorder}`,
        padding: "13px 16px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <PaneHeading style={{ marginBottom: 0, flex: 1 }}>Sublease recovery</PaneHeading>
        <span
          data-testid="sublease-recovery-count"
          style={{
            fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.blue,
            background: T.blueSoft, borderRadius: 999, padding: "2px 8px",
          }}
        >
          {records.length}
        </span>
        <span
          data-testid="sublease-recovery-currency"
          style={{
            fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.mid,
            background: T.tint, border: `1px solid ${T.border}`,
            borderRadius: 999, padding: "2px 8px",
          }}
        >
          {displayCcy || "USD"}
        </span>
      </div>

      {error ? (
        <div role="alert" data-testid="sublease-recovery-error" style={{ color: T.ragRed, fontSize: T.fsSm }}>
          Recovery records are unavailable.
        </div>
      ) : loading ? (
        <div data-testid="sublease-recovery-loading" style={{ color: T.mid, fontSize: T.fsSm }}>
          Loading recovery records…
        </div>
      ) : (
        <>
          <div
            data-testid="sublease-recovery-flow"
            style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}
          >
            <HeadlineTile
              testId="recovery-tile-head-rent"
              label="Head rent"
              primary={headRentDisplay}
              borderColor={T.subleaseRecoveryBorder}
            />
            <HeadlineTile
              testId="recovery-tile-sublease-income"
              label="Sublease income"
              primary={incomeDisplay}
              secondary={incomeSub}
              borderColor={T.subleaseRecoveryBorder}
              primaryColor={T.ragGreenText}
            />
            <HeadlineTile
              testId="recovery-tile-net-exposure"
              label="Net exposure"
              primary={netDisplay}
              secondary={netSub}
              borderColor={T.subleaseRecoveryBorder}
            />
          </div>

          {records.length === 0 ? (
            <div
              data-testid="sublease-recovery-empty"
              style={{ padding: "12px 2px 2px", fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}
            >
              No subtenant recovery records. Income not recorded; no sublease has been associated.
            </div>
          ) : (
            <div style={{ overflowX: "auto", marginTop: 12 }}>
              <table data-testid="sublease-recovery-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsXs }}>
                <thead>
                  <tr style={{ color: T.mid, textAlign: "left", borderBottom: `1px solid ${T.border}` }}>
                    {["Earning sub-out", "Area", "Term", "Status", "Annual income"].map((label) => (
                      <th key={label} style={{ padding: "7px 8px", fontWeight: T.fwSemibold }}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => {
                    const amount = record.annualIncome == null
                      ? "income not recorded"
                      : drawerMoney(Number(record.annualIncome), String(record.currency || "").trim() || nativeCurrency, displayCcy || record.currency || nativeCurrency, rates).text;
                    const formatDate = (value) => value instanceof Date
                      ? value.toISOString().slice(0, 10)
                      : String(value || "").slice(0, 10);
                    const term = [record.startDate, record.endDate]
                      .filter(Boolean)
                      .map(formatDate)
                      .join(" – ") || "Not recorded";
                    return (
                      <tr key={record.id} data-testid="sublease-recovery-row" style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: "8px", color: T.dark }}>
                          {record.sourceLeaseId ? (
                            <Link
                              to={buildLeaseUrl(record.sourceLeaseId)}
                              data-testid={`recovery-source-lease-${record.sourceLeaseId}`}
                              style={{ color: T.blue, textDecoration: "none" }}
                            >
                              {record.sourceLeaseId} · {record.sourceLeaseLessee || record.subtenant}
                            </Link>
                          ) : record.subtenant}
                        </td>
                        <td style={{ padding: "8px", color: T.dark }}>{record.areaSqft == null ? "—" : `${Number(record.areaSqft).toLocaleString("en-US")} sq ft`}</td>
                        <td style={{ padding: "8px", color: T.mid }}>{term}</td>
                        <td style={{ padding: "8px", color: T.mid }}>{record.status}</td>
                        <td style={{ padding: "8px", color: T.dark, whiteSpace: "nowrap" }}>{amount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {lease?.currency && lease.currency !== "USD" && (
        <div data-testid="sublease-recovery-usd-note" style={{ marginTop: 8, fontSize: T.fsXxs, color: T.light }}>
          Recovery flow is shown in {displayCcy || "USD"} where FX is available; otherwise values retain their labeled source currency.
        </div>
      )}
    </section>
  );
}

function remainingAllowances(allowances, abatement, nativeCcy, target, rates) {
  return sumDrawerMoney((allowances || [])
    .filter(a => (a.type === "abatement") === abatement && (abatement || !isExpired(a.expiryDate)))
    .map(a => ({ amount: Math.max(0, calcRemaining(a.totalAmount, a.drawnToDate) ?? 0), currency: a.currency || nativeCcy })), target, rates);
}

function FinancialsHeadlineStrip({ lease, computed, schedule, allowances, displayCcy, rates, extraTile = null, columns = null }) {
  const nativeCcy = lease?.currency || "USD";
  const effCcy    = displayCcy || nativeCcy;
  const headlines = leaseAnnualHeadlines(lease, computed);
  const headlineAmount = (amount, sourceCurrency = headlines.currency) => ({ amount, currency: sourceCurrency });

  function fmtAmt(nativeAmt) {
    if (nativeAmt && typeof nativeAmt === "object") return drawerMoney(nativeAmt.amount, nativeAmt.currency, effCcy, rates).text;
    if (nativeAmt == null || !Number.isFinite(Number(nativeAmt))) return "—";
    const n = Number(nativeAmt);
    const converted = nativeCcy === effCcy
      ? n
      : convertAmount(n, nativeCcy, effCcy, rates);
    const eff = (converted != null && Number.isFinite(converted)) ? converted : n;
    const ccy = (converted != null && Number.isFinite(converted)) ? effCcy : nativeCcy;
    return fmtMoney(eff, ccy);
  }

  function fmtPsf(nativeAmt) {
    if (nativeAmt && typeof nativeAmt === "object") return `${drawerMoney(nativeAmt.amount, nativeAmt.currency, effCcy, rates, { decimals: 2 }).text}/sf`;
    if (nativeAmt == null || !Number.isFinite(Number(nativeAmt))) return "—";
    const n = Number(nativeAmt);
    const converted = nativeCcy === effCcy
      ? n
      : convertAmount(n, nativeCcy, effCcy, rates);
    const eff = (converted != null && Number.isFinite(converted)) ? converted : n;
    const ccy = (converted != null && Number.isFinite(converted)) ? effCcy : nativeCcy;
    return `${fmtMoney(eff, ccy, { decimals: 2 })}/sf`;
  }

  // Rent CAGR: geometric mean of year-on-year contractual increases.
  let cagrStr = "—";
  const sourcedSchedule = (schedule || []).filter((row) => !row.isNotInSource);
  if (sourcedSchedule.length >= 2) {
    const first = Number(sourcedSchedule[0].contractual);
    const last  = Number(sourcedSchedule[sourcedSchedule.length - 1].contractual);
    const n     = sourcedSchedule.length - 1;
    if (first > 0 && last > 0 && n > 0) {
      const cagr = Math.pow(last / first, 1 / n) - 1;
      cagrStr = `~${(cagr * 100).toFixed(1)}%`;
    }
  }

  // Abatement left: remaining across all abatement-type allowances.
  let abatementLeft = 0;
  let hasAbatement  = false;
  for (const alw of (allowances || [])) {
    if (alw.type !== "abatement") continue;
    hasAbatement = true;
    const r = calcRemaining(alw.totalAmount, alw.drawnToDate);
    if (r != null && r > 0) abatementLeft += r;
  }

  // Allowance left: remaining across non-abatement, non-expired drawable allowances.
  // Expired allowances have forfeited their remaining balance — exclude them.
  let allowanceLeft = 0;
  let hasDrawable   = false;
  for (const alw of (allowances || [])) {
    if (alw.type === "abatement") continue;
    if (isExpired(alw.expiryDate)) continue;
    hasDrawable = true;
    const r = calcRemaining(alw.totalAmount, alw.drawnToDate);
    if (r != null && r > 0) allowanceLeft += r;
  }

  return (
    <div
      data-testid="financials-headline-strip"
      style={{
        display:               "grid",
        gridTemplateColumns:   columns ? `repeat(${columns}, minmax(0, 1fr))` : "repeat(6, 1fr)",
        gap:                   10,
      }}
    >
      <HeadlineTile
        testId="headline-tile-annual-rent"
        label="Annual rent"
        primary={headlines.basis !== "monthly_actual" && (hasRecoveryBasis(lease) || (!computed && lease?.annualRentBasis === "net_recorded_recoveries"))
          ? fmtMoney(netRentDisplay(lease, effCcy, rates).amount, netRentDisplay(lease, effCcy, rates).currency)
          : fmtAmt(headlineAmount(headlines.annual))}
        secondary={
          headlines.basis === "monthly_actual" ? `sourced actual payable (${new Date().getFullYear()}) · net of abatement`
            : hasRecoveryBasis(lease) ? <AnnualRentBreakdown lease={lease} displayCcy={columns ? displayCcy : undefined} rates={rates} /> : headlines.net != null
            ? `contractual (${new Date().getFullYear()}) · net ${fmtAmt(headlineAmount(headlines.net))}`
            : null
        }
      />
      <HeadlineTile
        testId="headline-tile-rate"
        label="Rate"
        primary={headlines.netPsf == null ? "Unavailable" : fmtPsf(headlineAmount(headlines.netPsf))}
        secondary={
          headlines.contractualPsf != null
            ? `${fmtPsf(headlineAmount(headlines.contractualPsf, headlines.contractualCurrency))} contractual`
            : null
        }
      />
      <HeadlineTile
        testId="headline-tile-total-lifetime"
        label="Total lifetime"
        primary={fmtAmt(computed?.totalLifetimeCost)}
      />
      <HeadlineTile
        testId="headline-tile-rent-cagr"
        label="Rent CAGR"
        primary={cagrStr}
      />
      {/* n/a when lease has no abatement clause — concept doesn't apply */}
      <HeadlineTile
        testId="headline-tile-abatement-left"
        label="Abatement left"
        primary={hasAbatement ? columns ? remainingAllowances(allowances, true, nativeCcy, effCcy, rates) : fmtAmt(abatementLeft) : "n/a"}
      />
      {/* n/a when lease has no drawable allowance clause — concept doesn't apply */}
      <HeadlineTile
        testId="headline-tile-allowance-left"
        label="Allowance left"
        primary={hasDrawable ? columns ? remainingAllowances(allowances, false, nativeCcy, effCcy, rates) : fmtAmt(allowanceLeft) : "n/a"}
      />
      {extraTile}
    </div>
  );
}

// ─── ConcessionSubheading ─────────────────────────────────────────────────────
// Divider-style subsection label following the "concsub" pattern from the comp.
// Label text + flex-1 hairline rule on the right.

function ConcessionSubheading({ children }) {
  return (
    <div
      data-testid="concession-subheading"
      style={{
        display:     "flex",
        alignItems:  "center",
        gap:         8,
        fontSize:    T.fsXxs5,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        fontWeight:  T.fwBold,
        color:       T.mid,
        margin:      "4px 2px 9px",
      }}
    >
      {children}
      <span style={{ flex: 1, height: 1, background: T.border }} />
    </div>
  );
}

// ─── Year-format countdown helper ─────────────────────────────────────────────
// Returns a human-readable countdown in year units for DatesAheadPane rows.
//   days < 0   → "Past"
//   days < 183 → "< 1 yr"
//   otherwise  → "~N yr(s)"

function fmtYearsCountdown(days) {
  if (days == null) return null;
  if (days < 0) return "Past";
  const years = Math.round(days / 365.25);
  if (years < 1) return "< 1 yr";
  return `~${years} yr${years === 1 ? "" : "s"}`;
}

// ─── KeyTermsPane ─────────────────────────────────────────────────────────────
// Row 1, right cell of the Overview 2×3 grid. Replaces RentAndTermPane.
//
// Header:  "Key terms" | "As of <Month Year>" | CurrencyToggle
// Content: 8 neutral tiles (4×2 grid) — Size, Type, Structure, Term,
//          Annual rent, Next expiry, Renewals, Deposit
// Footer:  LeaseTimeline (term-runway slider) + SpacebaseBanner
//
// Tiles are neutral: muted uppercase label, dark value, optional sub-line,
// tint background, T.border border, 10 px radius. No accent colour bars.

function KeyTermsPane({
  selectedLease, computed, displayCcy, rates, onDisplayCcyChange,
  today, criticalDates, decisions, allowances,
  officeStage, recordedRent, headerAction = null, compact = false,
}) {
  const nativeCcy = selectedLease?.currency || "USD";
  const nowLabel  = today.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const pastDueActionable = isActionablePastDueExpiry(selectedLease, {
    officeStage,
    recordedRent,
  });

  // ── Tile data ─────────────────────────────────────────────────────────────

  // 1. Type
  const typeValue = leaseTypeLabel(selectedLease?.type) || null;

  // 2. Structure (opexStructure)
  const structureValue = selectedLease?.opexStructure || null;

  // 3. Size
  const sizeValue = leaseSizeDisplay(selectedLease);
  const { activeCount: areaCount, expiredCount } = leaseAreaPresentation(selectedLease, today);
  const count = <>{areaCount} active {areaCount === 1 ? "area" : "areas"}
    {expiredCount > 0 && <span style={{ color: T.light, fontWeight: T.fwRegular }}> - {expiredCount} expired</span>}</>;
  const sizeHint = areaCount > 1 ? (
    <a
      data-testid="lease-size-areas-link"
      href="#lease-areas-strip"
      style={{ color: T.blue, fontWeight: T.fwSemibold, textDecoration: "none" }}
    >
      {count} ↓
    </a>
  ) : selectedLease?.areas?.length ? <span data-testid="lease-size-current-area-count">{count}</span> : null;

  // 4. Term
  const termValue = selectedLease?.termType || null;
  const startDt = selectedLease?.startDate ? parseDateOnly(selectedLease.startDate) : null;
  const endDtFixed = selectedLease?.endDate && !isSentinelEndDate(selectedLease.endDate)
    ? parseDateOnly(selectedLease.endDate) : null;
  const startFmt = startDt
    ? startDt.toLocaleDateString("en-US", { month: "short", year: "numeric" }) : null;
  const endFmt = endDtFixed
    ? (isPastExpiryDate(selectedLease.endDate)
      ? formatLongExpiryDate(selectedLease.endDate)
      : endDtFixed.toLocaleDateString("en-US", { month: "short", year: "numeric" }))
    : (isSentinelEndDate(selectedLease?.endDate) ? "Ongoing" : null);
  const termMonths = computed?.termLengthMonths;
  const termDateRange = [startFmt, endFmt].filter(Boolean).join(" – ");
  const termSub = termDateRange
    ? (termMonths ? `${termDateRange} · ~${Math.round(termMonths / 12)} yrs` : termDateRange)
    : null;

  // 5. Annual rent (converted to displayCcy)
  const annualHeadline = leaseAnnualHeadlines(selectedLease, computed);
  const annualRentNative = convertAmount(annualHeadline.annual, annualHeadline.currency, nativeCcy, rates);
  const annualRentConverted = convertAmount(annualHeadline.annual, annualHeadline.currency, displayCcy, rates);
  const annualRentAmt = annualRentConverted ?? annualRentNative;
  const annualRentCcy = annualRentConverted != null ? displayCcy : nativeCcy;
  const annualRentDisplay = annualHeadline.basis !== "monthly_actual" && (hasRecoveryBasis(selectedLease) || (!computed && selectedLease?.annualRentBasis === "net_recorded_recoveries"))
    ? (selectedLease.annualRentUsd == null ? null
      : fmtMoneyCompact(netRentDisplay(selectedLease, displayCcy, rates).amount, netRentDisplay(selectedLease, displayCcy, rates).currency))
    : annualRentAmt != null ? fmtMoneyCompact(annualRentAmt, annualRentCcy) : null;

  // 6. Next expiry
  let expiryDisplay = null;
  let expirySub = null;
  if (isSentinelEndDate(selectedLease?.endDate)) {
    expiryDisplay = "Ongoing";
  } else if (selectedLease?.endDate) {
    const expiryDt = parseDateOnly(selectedLease.endDate);
    if (expiryDt) {
      expiryDisplay = isPastExpiryDate(selectedLease.endDate)
        ? formatLongExpiryDate(selectedLease.endDate)
        : expiryDt.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      const daysLeft = Math.round((expiryDt.getTime() - today.getTime()) / 86400000);
      const yrsLeft  = (Math.max(0, daysLeft) / 365.25).toFixed(1);
      const spanMs   = startDt ? expiryDt.getTime() - startDt.getTime() : null;
      const elapsedMs = startDt ? today.getTime() - startDt.getTime() : null;
      const pctElapsed = spanMs != null && elapsedMs != null && spanMs > 0
        ? Math.round(Math.min(100, Math.max(0, elapsedMs / spanMs) * 100)) : null;
      expirySub = `~${yrsLeft} yrs left${pctElapsed != null ? ` · ${pctElapsed}% elapsed` : ""}`;
    }
  }

  // 7. Renewals
  const renewalCount     = selectedLease?.renewalCount;
  const renewalTermYears = selectedLease?.renewalTermYears;
  const renewalDisplay   = renewalCount != null && renewalCount !== ""
    ? (renewalTermYears ? `${renewalCount} × ${renewalTermYears}-yr` : String(renewalCount))
    : null;

  // 8. Deposit (converted to displayCcy, same pattern as Annual rent)
  const depositRaw = selectedLease?.securityDepositAmount;
  const depositNative = depositRaw != null && depositRaw !== "" ? parseFloat(depositRaw) : null;
  const depositConverted = convertAmount(depositNative, nativeCcy, displayCcy, rates);
  const depositAmt = depositConverted ?? depositNative;
  const depositCcy = depositConverted != null ? displayCcy : nativeCcy;
  const depositDisplay = depositAmt != null && depositAmt > 0
    ? fmtMoneyCompact(depositAmt, depositCcy) : null;
  const depositSub = selectedLease?.securityDepositType || null;

  const tiles = [
    { key: "size",        label: "Size",        value: sizeValue,         sub: sizeHint },
    { key: "type",        label: "Type",        value: typeValue,         sub: null },
    { key: "structure",   label: "Structure",   value: structureValue,    sub: null },
    { key: "term",        label: "Term",        value: termValue,         sub: termSub },
    { key: "annual-rent", label: "Annual rent", value: annualRentDisplay, sub: annualHeadline.basis === "monthly_actual"
      ? `sourced actual payable · ${new Date().getFullYear()}` : hasRecoveryBasis(selectedLease) ? <AnnualRentBreakdown lease={selectedLease} /> : `contractual · ${new Date().getFullYear()} recorded` },
    {
      key: "next-expiry",
      label: "Next expiry",
      value: expiryDisplay,
      sub: expirySub,
      valueColor: pastDueActionable ? T.ragRed : undefined,
    },
    { key: "renewals",    label: "Renewals",    value: renewalDisplay,    sub: renewalDisplay ? "option" : null },
    { key: "deposit",     label: "Deposit",     value: depositDisplay,    sub: depositSub },
  ];

  return (
    <section
      data-testid="key-terms-pane"
      style={{
        background:    T.card,
        border:        `1px solid ${T.border}`,
        borderRadius:  6,
        padding:       "11px 13px",
        display:       "flex",
        flexDirection: "column",
        height:        "100%",
        minHeight:     420,
        boxSizing:     "border-box",
      }}
    >
      {/* Header row: "Key terms" · as-of label · currency toggle */}
      <div
        style={{
          display:      "flex",
          alignItems:   "center",
          gap:          10,
          marginBottom: 12,
          flexWrap:     "wrap",
        }}
      >
        <PaneHeading style={{ marginBottom: 0, flex: 1, minWidth: 0 }}>
          Key terms
        </PaneHeading>
        <span
          data-testid="key-terms-as-of"
          style={{ fontSize: T.fsXxs, color: T.mid, whiteSpace: "nowrap" }}
        >
          As of {nowLabel}
        </span>
        <CurrencyToggle
          nativeCurrency={nativeCcy}
          value={displayCcy}
          onChange={onDisplayCcyChange}
        />
        {headerAction}
      </div>

      {/* 8-tile neutral grid — 4 columns, 2 rows */}
      <div
        data-testid="key-terms-grid"
        style={{
          display:             "grid",
          gridTemplateColumns: compact ? "repeat(4, minmax(0, 1fr))" : "repeat(4, 1fr)",
          gap:                 8,
        }}
      >
        {tiles.map((tile) => (
          <div
            key={tile.key}
            data-testid={`key-terms-tile-${tile.key}`}
            style={{
              background:    T.card,
              border:        `1px solid ${T.border}`,
              borderRadius:  8,
              padding:       "7px 10px",
              minWidth:      0,
            }}
          >
            <div
              style={{
                fontSize:      T.fsXxxs5,
                fontWeight:    T.fwBold,
                color:         T.mid,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom:  4,
                whiteSpace:    "nowrap",
                overflow:      "hidden",
                textOverflow:  "ellipsis",
              }}
            >
              {tile.label}
            </div>
            <div
              style={{
                fontSize:     T.fsMd,
                fontWeight:   T.fwDisplay,
                color:        tile.valueColor ?? (tile.value ? T.dark : T.light),
                lineHeight:   1.1,
                marginBottom: 2,
                whiteSpace:   "nowrap",
                overflow:     "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {tile.value || "—"}
            </div>
            {tile.sub && (
              <div style={{ fontSize: T.fsXxxs, color: T.light, lineHeight: 1.3 }}>
                {tile.sub}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Term-runway slider — directly below the 8-tile grid */}
      <div style={{ marginTop: 10 }}>
        <LeaseTimeline
          startDate={selectedLease?.startDate}
          endDate={selectedLease?.endDate}
          soonestPossibleExpiration={selectedLease?.soonestPossibleExpiration}
          nextNoticeDeadline={selectedLease?.nextNoticeDeadline}
          today={today}
          criticalDates={criticalDates}
          decisions={decisions}
          abatementRows={(allowances || []).filter((a) => a.type === "abatement")}
          option2Date={selectedLease?.breakDate}
          hideDecisions={true}
        />
      </div>

      {/* Source attribution — pinned at bottom */}
      <div style={{ marginTop: "auto", paddingTop: 10 }}>
        <SpacebaseBanner
          leaseIdSpacebase={selectedLease?.leaseIdSpacebase}
          lastCsvImportedAt={selectedLease?.lastCsvImportedAt}
        />
      </div>
    </section>
  );
}

// ─── AllTermsExpander ─────────────────────────────────────────────────────────
// Collapsed row at the bottom of the Overview tab.
// Shows clause categories in a summary line; "Show ▾" / "Hide ▴" toggle.
// Expands inline to render the three TermsPaneCard groups.
// Collapsed state is component-local (useState); no URL persistence.

function AllTermsExpander({ selectedLease, locationId, leaseId, canWrite, onEditOpen }) {
  const [open, setOpen] = React.useState(false);

  const panelByKey    = Object.fromEntries(ACCORDION_PANELS.map((p) => [p.key, p]));
  const opExPanel     = panelByKey["operating_expenses"];
  const optRightsPanel = panelByKey["options_rights"];
  const restrRestPanel = panelByKey["restrictions_restoration"];

  return (
    <div
      data-testid="all-terms-expander"
      style={{
        background:   T.tint,
        border:       `1px solid ${T.border}`,
        borderRadius: 10,
        overflow:     "hidden",
      }}
    >
      {/* Toggle row */}
      <button
        type="button"
        data-testid="all-terms-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          width:          "100%",
          display:        "flex",
          alignItems:     "center",
          gap:            10,
          padding:        "10px 14px",
          background:     "none",
          border:         "none",
          cursor:         "pointer",
          textAlign:      "left",
        }}
      >
        <span
          style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark, flexShrink: 0 }}
        >
          All terms
        </span>
        <span
          style={{ fontSize: T.fsXs, color: T.mid, flex: 1 }}
        >
          Operating Expenses · Options &amp; Rights · Restrictions &amp; Restoration
        </span>
        <span
          data-testid="all-terms-toggle-label"
          style={{ fontSize: T.fsXs, color: T.blue, fontWeight: T.fwSemibold, flexShrink: 0 }}
        >
          {open ? "Hide ▴" : "Show ▾"}
        </span>
      </button>

      {/* Expanded content */}
      {open && (
        <div
          data-testid="all-terms-content"
          style={{
            padding:       "0 14px 14px",
            display:       "flex",
            flexDirection: "column",
            gap:           12,
          }}
        >
          {opExPanel && (
            <TermsPaneCard
              title={opExPanel.label}
              panelKey={opExPanel.key}
              fields={opExPanel.fields}
              lease={selectedLease}
              locationId={locationId}
              leaseId={leaseId}
              canWrite={canWrite}
              onEditOpen={onEditOpen}
            />
          )}
          {optRightsPanel && (
            <TermsPaneCard
              title={optRightsPanel.label}
              panelKey={optRightsPanel.key}
              fields={optRightsPanel.fields}
              lease={selectedLease}
              locationId={locationId}
              leaseId={leaseId}
              canWrite={canWrite}
              onEditOpen={onEditOpen}
            />
          )}
          {restrRestPanel && (
            <TermsPaneCard
              title={restrRestPanel.label}
              panelKey={restrRestPanel.key}
              fields={restrRestPanel.fields}
              lease={selectedLease}
              locationId={locationId}
              leaseId={leaseId}
              canWrite={canWrite}
              onEditOpen={onEditOpen}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── DatesAheadPane ───────────────────────────────────────────────────────────
// Row 3, right cell of the Overview 2×3 grid.
//
// Merges two item types into a single chronologically-sorted list:
//   KEY DATE  — the lease expiry (endDate), shown first if soonest
//   DECISION  — renewal-option and other decision deadlines from buildDecisionsItems()
//
// Countdowns are rendered in year format (~N yrs / < 1 yr / Past) rather than
// the day-count pills used on the Dates tab.

function DatesAheadPane({ selectedLease, decisions, today, officeStage, recordedRent }) {
  const items = [];
  const actionablePastExpiry = isActionablePastDueExpiry(selectedLease, {
    officeStage,
    recordedRent,
  });

  // Lease expiry → KEY DATE row
  // Track the expiry date string for deduplication against decision rows.
  let expiryDateKey = null;
  if (
    selectedLease?.endDate &&
    !isSentinelEndDate(selectedLease.endDate)
  ) {
    const dt = parseDateOnly(selectedLease.endDate);
    if (dt) {
      expiryDateKey = String(selectedLease.endDate).slice(0, 10);
      const days = Math.ceil(
        (dt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );
      items.push({
        tag:      "KEY DATE",
        tagColor: T.spacebaseBlue,
        title:    "Lease expiry",
        subtitle: isPastExpiryDate(selectedLease.endDate)
          ? formatLongExpiryDate(selectedLease.endDate)
          : dt.toLocaleDateString("en-US", { year: "numeric", month: "short" }),
        subtitleColor: actionablePastExpiry ? T.ragRed : undefined,
        days,
      });
    }
  }

  // Renewal / decision deadlines → DECISION rows
  // Skip any decision whose date matches the lease expiry (deduplication).
  for (const d of (decisions || [])) {
    if (d.daysToExpiry != null) {
      const dDateKey = d.date ? String(d.date).slice(0, 10) : null;
      if (expiryDateKey && dDateKey === expiryDateKey) continue;
      const dt = parseDateOnly(d.date);
      items.push({
        tag:      "DECISION",
        tagColor: T.amberDark,
        title:    d.label,
        subtitle: dt
          ? dt.toLocaleDateString("en-US", { year: "numeric", month: "short" })
          : null,
        days: d.daysToExpiry,
      });
    }
  }

  // Sort ascending by days remaining
  items.sort((a, b) => a.days - b.days);

  return (
    <section
      data-testid="dates-ahead-pane"
      style={{
        background:    T.card,
        border:        `1px solid ${T.border}`,
        borderRadius:  6,
        padding:       "11px 13px",
        display:       "flex",
        flexDirection: "column",
        height:        "100%",
        boxSizing:     "border-box",
      }}
    >
      <PaneHeading style={{ marginBottom: 10 }}>Dates ahead</PaneHeading>

      {items.length === 0 ? (
        <div
          style={{
            fontSize:  T.fsXs,
            color:     T.light,
            fontStyle: "italic",
          }}
        >
          No upcoming dates recorded yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {items.map((item, i) => (
            <div
              key={`${item.tag}-${i}`}
              data-testid={`dates-ahead-row-${i}`}
              style={{
                display:      "flex",
                alignItems:   "center",
                gap:          10,
                padding:      "7px 0",
                borderBottom: i < items.length - 1
                  ? `1px solid ${T.border}`
                  : "none",
              }}
            >
              {/* Tag chip */}
              <span
                data-testid="dates-ahead-tag"
                style={{
                  fontSize:      T.fsXxs,
                  fontWeight:    T.fwBold,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  padding:       "1px 6px",
                  borderRadius:  4,
                  background:    `${item.tagColor}1a`,
                  color:         item.tagColor,
                  flexShrink:    0,
                  whiteSpace:    "nowrap",
                }}
              >
                {item.tag}
              </span>

              {/* Title + subtitle */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize:     T.fsSm,
                    fontWeight:   T.fwSemibold,
                    color:        T.dark,
                    overflow:     "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace:   "nowrap",
                  }}
                >
                  {item.title}
                </div>
                {item.subtitle && (
                  <div style={{ fontSize: T.fsXxs, color: item.subtitleColor ?? T.mid }}>
                    {item.subtitle}
                  </div>
                )}
              </div>

              {/* Year countdown */}
              <span
                data-testid={`dates-ahead-countdown-${i}`}
                style={{
                  fontSize:   T.fsSm,
                  fontWeight: T.fwSemibold,
                  color:      T.dark,
                  flexShrink: 0,
                }}
              >
                {fmtYearsCountdown(item.days)}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── LeaseDetailBody ──────────────────────────────────────────────────────

function LeaseDetailListOwner({
  leaseId,
  locationId,
  currentRole,
  refreshKey,
  activeTab = "overview",
  onDocumentsCount,
  onTabChange,
  onAreaLabelChange,
  onLeaseLoaded,
  // Task #9096 optional drawer variant. Defaults keep the full reader unchanged.
  variant = "page",
  recordStatus = null,
  onOpenOffice,
  onBusyChange,
  onLeaseSaved,
  onSupportingLoaded,
  currencyControl,
  asOf,
}) {
  const isDrawer = variant === "drawer";
  const [readErrors, setReadErrors] = useState([]);
  const [strictReloadKey, setStrictReloadKey] = useState(0);
  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [primaryLease,    setPrimaryLease]    = useState(undefined); // undefined = loading
  const [allLeases,       setAllLeases]       = useState([]);
  const [criticalDates,   setCriticalDates]   = useState([]);
  const [allowances,      setAllowances]      = useState([]);
  const [recoveryRecords, setRecoveryRecords] = useState([]);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError,   setRecoveryError]   = useState(false);
  const [documents,       setDocuments]       = useState([]);
  const [locationData,         setLocationData]         = useState(null);
  const [loading,              setLoading]              = useState(true);
  const [error,                setError]                = useState(null);
  const [localDisplayCcy, setLocalDisplayCcy] = useState("USD");
  const displayCcy = currencyControl?.value ?? localDisplayCcy;
  const setDisplayCcy = currencyControl?.onChange ?? setLocalDisplayCcy;
  const [accordionOpen,        setAccordionOpen]        = useState({ rent_schedule: true });
  const [selectedLeaseId,      setSelectedLeaseId]      = useState(leaseId);
  const [allowancesRefreshKey, setAllowancesRefreshKey] = useState(0);
  const [areaRefreshKey, setAreaRefreshKey] = useState(0);
  const [today] = useState(() => asOf ?? new Date());
  const localFx = useFxRates();
  const rates = currencyControl ? currencyControl.rates : localFx.rates;
  const { basis: fxBasis, rates: fxBasisRates, liveFetchedAt: fxLiveFetchedAt } = useFxConversionBasis();

  const toggleAccordion = useCallback(
    (key) => setAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] })),
    [],
  );

  // refreshAllowances: re-fetches only allowances after a draw is recorded.
  // Does not trigger a full page reload, so stateful panes stay intact.
  const refreshAllowances = useCallback(() => {
    setAllowancesRefreshKey((k) => k + 1);
  }, []);

  // Patch both sources consumed by selectLeaseDetail. The list wins its
  // shallow merge, and a selected non-primary lease comes only from the list.
  // Use the request's lease ID, not whichever lease is selected at completion.
  const applyGeneratedSummary = useCallback((generatedLeaseId, result) => {
    const summaryPatch = {
      aiSummary: result.aiSummary,
      aiSummaryAt: result.aiSummaryAt,
      aiSummarySource: result.aiSummarySource,
      updatedAt: result.updatedAt,
    };
    setPrimaryLease((primary) => primary?.id === generatedLeaseId
      ? { ...primary, ...summaryPatch }
      : primary);
    setAllLeases((rows) => rows.map((row) => row.id === generatedLeaseId
      ? { ...row, ...summaryPatch }
      : row));
  }, []);

  // Other mutations (including deleting a lease) must refresh both inputs of
  // the selected-lease merge, not only the location's active summary.
  const refreshPrimaryLease = useCallback(async () => {
    if (!locationId) return;
    try {
      const [primary, rows] = await Promise.all([
        getActiveLeaseSummary(locationId),
        listLocationLeases(locationId),
      ]);
      setPrimaryLease(primary);
      setAllLeases(rows);
    } catch (err) {
      log.error("LeaseDetailBody: lease refresh failed", { locationId, err: String(err) });
      setError(new Error("Lease data could not refresh. Please reload to see the latest saved changes."));
    }
  }, [locationId]);

  // EditLeaseModal open state — must be declared before any early return
  // (Rules of Hooks). Opened by "+ add" buttons in the Terms pane cards.
  const [editModalOpen,    setEditModalOpen]    = useState(false);
  const [editInitialField, setEditInitialField] = useState(null);
  const handleEditOpen = useCallback((field) => {
    setEditInitialField(field ?? null);
    setEditModalOpen(true);
  }, []);

  const supportingRef = useRef(onSupportingLoaded);
  supportingRef.current = onSupportingLoaded;
  useEffect(() => {
    if (loading) return;
    supportingRef.current?.({ leaseId, criticalDates, location: locationData, readErrors });
  }, [loading, leaseId, criticalDates, locationData, readErrors]);

  const busyChangeRef = useRef(onBusyChange);
  busyChangeRef.current = onBusyChange;
  useEffect(() => {
    busyChangeRef.current?.({ editing: editModalOpen, generating: summaryGenerating });
  }, [editModalOpen, summaryGenerating]);

  // Re-fetch only allowances when a draw is recorded (allowancesRefreshKey > 0).
  // Runs independently of the main data load so stateful panes stay intact.
  useEffect(() => {
    if (allowancesRefreshKey === 0) return;
    if (!locationId || !leaseId) return;
    listAllowances(locationId, leaseId)
      .then((alws) => setAllowances(alws || []))
      .catch(() => {});
  }, [allowancesRefreshKey, locationId, leaseId]);

  // Re-pin selected lease whenever the URL-driven leaseId changes.
  useEffect(() => {
    setSelectedLeaseId(leaseId);
  }, [leaseId]);

  // Lift the document count to the parent (LeaseDetailPage) for the tab badge.
  useEffect(() => {
    if (typeof onDocumentsCount === "function") {
      onDocumentsCount(documents.length);
    }
  }, [documents, onDocumentsCount]);

  useEffect(() => {
    if (!locationId || !leaseId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Drawer variant: supporting read failures stay visible + retryable
    // instead of becoming confirmed-absent evidence. Page behavior unchanged.
    const failed = [];
    const soft = (label, fallback, fn) => (err) => {
      if (fn) fn(err);
      if (isDrawer) failed.push({ label, message: String(err?.message || err) });
      return fallback;
    };

    Promise.all([
      getActiveLeaseSummary(locationId).catch(soft("Lease summary", null, (err) => {
        log.error("LeaseDetailBody: getActiveLeaseSummary failed", { locationId, err: String(err) });
      })),
      listLocationLeases(locationId).catch(soft("Leases at this office", [])),
      listCriticalDates(locationId, leaseId).catch(soft("Critical dates", [])),
      listAllowances(locationId, leaseId).catch(soft("Allowances", [])),
      isDrawer ? Promise.resolve([]) : listDocuments(locationId, leaseId).catch(() => []),
      getLocation(locationId).catch(soft("Office", null)),
    ]).then(([primary, all, cds, alws, docs, loc]) => {
      if (cancelled) return;
      setReadErrors(failed);
      setPrimaryLease(primary ?? null);
      setAllLeases(all || []);
      setCriticalDates(cds || []);
      setAllowances(alws || []);
      setDocuments(docs || []);
      setLocationData(loc ?? null);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [leaseId, locationId, refreshKey, areaRefreshKey, strictReloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resolve the displayed lease ───────────────────────────────────────────
  //
  // primaryLease (from getActiveLeaseSummary) has the full computed block and
  // yearCosts. If the URL-pinned leaseId matches the primary, use it directly.
  // Otherwise fall back to the allLeases list entry (no computed block) or
  // the primary as a last resort so the page never renders empty.
  //
  // Must be declared before any conditional return to satisfy Rules of Hooks.

  const selectedLease = useMemo(
    () => selectLeaseDetail(primaryLease, allLeases, selectedLeaseId),
    [primaryLease, allLeases, selectedLeaseId],
  );
  const isPinnedLease = selectedLease?.id === selectedLeaseId;
  useEffect(() => {
    onLeaseLoaded?.(!loading && isPinnedLease ? selectedLease : null);
  }, [loading, isPinnedLease, selectedLease, onLeaseLoaded]);
  const hasLinkedSubOuts = isPinnedLease
    && allLeases.some((candidate) => candidate.headLeaseId === selectedLease.id);
  const linkedHeadLease = isPinnedLease && selectedLease?.headLeaseId
    ? allLeases.find((candidate) => candidate.id === selectedLease.headLeaseId) ?? null
    : null;

  useEffect(() => {
    if (!isPinnedLease || !selectedLease?.id || !hasLinkedSubOuts || selectedLease.subleaseRecoveryEnabled !== true || selectedLease.type !== "direct_lease") {
      setRecoveryRecords([]);
      setRecoveryLoading(false);
      setRecoveryError(false);
      return;
    }
    let cancelled = false;
    setRecoveryRecords([]);
    setRecoveryLoading(true);
    setRecoveryError(false);
    listLeaseRecoveries(locationId, selectedLease.id)
      .then((rows) => {
        if (!cancelled) setRecoveryRecords(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        log.error("LeaseDetailBody: recovery records failed", { leaseId: selectedLease.id, err: String(err) });
        if (!cancelled) setRecoveryError(true);
      })
      .finally(() => {
        if (!cancelled) setRecoveryLoading(false);
      });
    return () => { cancelled = true; };
  }, [locationId, selectedLease?.id, selectedLease?.subleaseRecoveryEnabled, selectedLease?.type, hasLinkedSubOuts, isPinnedLease]);

  useEffect(() => {
    if (typeof onAreaLabelChange !== "function" || !selectedLease?.id) return;
    onAreaLabelChange(
      selectedLease.id,
      leaseAreaSummary(selectedLease, locationData?.suite, today),
    );
  }, [selectedLease, locationData?.suite, onAreaLabelChange, today]);

  // ── Loading / error ───────────────────────────────────────────────────────

  if (loading && !primaryLease) {
    return (
      <div style={{ padding: 20, color: T.mid, fontSize: T.fsBase }}>
        Loading lease detail…
      </div>
    );
  }

  if (error) {
    return <ErrorBanner message={error.message || "Failed to load lease detail."} />;
  }

  const monthlyRead = isDrawer && selectedLease ? monthlyPayableEvidence(selectedLease) : null;
  const visibleReadErrors = monthlyRead?.state === "invalid"
    ? [...readErrors, { label: "Monthly payable evidence", message: monthlyRead.issue }]
    : readErrors;
  const readErrorBanner = isDrawer && visibleReadErrors.length > 0 ? (
    <div role="alert" data-testid="lease-drawer-read-errors"
      style={{ ...PANEL_CARD, borderColor: T.ragRed, display: "flex", alignItems: "center", gap: 10, marginBottom: 12, fontSize: T.fsXs, color: T.dark }}>
      <span style={{ flex: 1 }}>
        Some lease details couldn't be loaded: {visibleReadErrors.map((e) => e.label).join(", ")}. Shown sections may be incomplete; nothing missing here is confirmed absent.
      </span>
      <button type="button" data-testid="lease-drawer-read-retry" onClick={() => setStrictReloadKey((k) => k + 1)}
        style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 8, padding: "4px 10px", fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", color: T.dark }}>
        Retry
      </button>
    </div>
  ) : null;
  if (isDrawer && !selectedLease && readErrors.length > 0) return readErrorBanner;

  if (!selectedLease) {
    return (
      <div style={{ padding: 20, color: T.mid, fontSize: T.fsBase }}>
        Lease not found.
      </div>
    );
  }

  // Computed block is available only when selectedLease IS the primary.
  const computed = selectedLease?.id === primaryLease?.id ? primaryLease?.computed : null;
  const detailRecordedRent = computed?.annualRentRunRate
    ?? computed?.annualRent
    ?? selectedLease?.annualRentUsd
    ?? selectedLease?.listDisplayRentUsd
    ?? selectedLease?._listRentUsd
    ?? null;
  const actionabilityOfficeStage = locationData?.stage ?? selectedLease?.locationStage;

  const currentYear = today.getFullYear();
  const canWrite    = currentRole === "admin" || currentRole === "editor";

  // Hero bar uses all leases with computed merged in for the primary lease.
  const heroLeases = primaryLease
    ? allLeases.map((l) =>
        l.id === primaryLease.id ? { ...l, computed: primaryLease.computed, rentSchedule: primaryLease.rentSchedule,
          yearCosts: primaryLease.yearCosts, monthlyCosts: primaryLease.monthlyCosts, monthlyCostsStatus: primaryLease.monthlyCostsStatus } : l,
      )
    : allLeases;

  // Rent schedule: built from lease yearCosts + allowances.
  const schedule = buildRentSchedule(selectedLease, allowances, today);

  // Decisions ahead: upcoming critical-date markers for the timeline.
  const decisions = buildDecisionsItems(selectedLease, today, criticalDates);

  // ── Render ────────────────────────────────────────────────────────────────

  const bodyStyle = {
    display:       "flex",
    flexDirection: "column",
    gap:           12,
    padding:       "0 0 24px",
  };
  const nativeCurrency = selectedLease.currency || "USD";
  const nativeCurrencyUnitsPerUsd = nativeCurrency === "USD" ? 1 : Number(rates?.[nativeCurrency]);
  const nativeToUsdRate = Number.isFinite(nativeCurrencyUnitsPerUsd) && nativeCurrencyUnitsPerUsd > 0
    ? 1 / nativeCurrencyUnitsPerUsd
    : null;
  const nativeRateDetails = fxBasisRates.find((rate) => rate.currency === nativeCurrency);
  const rateMethod = nativeRateDetails
    ? [
        nativeRateDetails.source || "unknown source",
        nativeRateDetails.rateDate ? `rate date ${nativeRateDetails.rateDate}` : null,
        nativeRateDetails.carried ? "carried rate" : null,
      ].filter(Boolean).join(" · ")
    : "rate method unavailable";
  const financialsFxTooltip = [
    `Native currency: ${nativeCurrency}.`,
    nativeToUsdRate == null
      ? `Native-to-USD rate unavailable for ${nativeCurrency}.`
      : `Conversion used: 1 ${nativeCurrency} = ${nativeToUsdRate.toFixed(6)} USD (${nativeCurrencyUnitsPerUsd.toFixed(6)} ${nativeCurrency} per USD).`,
    `Saved basis: ${formatFxBasisSummary(fxBasis, fxLiveFetchedAt)}.`,
    `Rate method: ${rateMethod}.`,
  ].join(" ");

  return (
    <div data-testid="lease-detail-body" data-variant={variant}>
      {readErrorBanner}
      {isDrawer && currencyControl?.loading && <div role="status" style={{ fontSize: T.fsXs, color: T.mid }}>FX loading · values without a conversion retain their source currency.</div>}
      {isDrawer && currencyControl?.error && <div role="alert" style={{ fontSize: T.fsXs, color: T.mid }}>FX couldn't load · values without a conversion retain their source currency.</div>}

      {/* ── Overview pane ─────────────────────────────────────────────────
          LEASES-OVERVIEW-REDESIGN: true 2-column × 3-row CSS grid.
          6 direct children — each column is minmax(0, 1fr):
            Row 1: LocationPropertyPane (L) | RentAndTermPane (R)
            Row 2: LeaseSummaryCard(summary) (L) | FinancialsAtAGlanceCard (R)
            Row 3: LeaseSummaryCard(parties) (L) | DatesAheadPane (R)
          Provenance badge legend sits below the grid.
      */}
      {isDrawer && (
        <div data-testid="lease-drawer-overview" style={{ ...bodyStyle, ...paneStyle(activeTab === "overview") }}>
          <DrawerKeyTermsPane
            dateReadState={readErrors.some(e => e.label === "Critical dates") ? "failed" : "ready"}
            lease={selectedLease}
            computed={computed}
            displayCcy={displayCcy}
            rates={rates}
            onDisplayCcyChange={setDisplayCcy}
            today={today}
            criticalDates={criticalDates}
            recordStatus={recordStatus}
            canWrite={canWrite}
            onEdit={() => handleEditOpen(null)}
          />
          <div data-testid="overview-pane-lease-summary">
            <LeaseSummaryCard
              lease={selectedLease}
              computed={computed}
              today={today}
              variant="summary"
              leases={heroLeases}
              canWrite={canWrite}
              onGenerated={applyGeneratedSummary}
              onGeneratingChange={setSummaryGenerating}
              measuredProse
            />
          </div>
          <DrawerPremisesPane
            location={locationData}
            lease={selectedLease}
            siblingLeases={allLeases}
            today={today}
            locationError={readErrors.some((e) => /location|office/i.test(e.label || ""))}
            onOpenOffice={onOpenOffice}
            onEditAreas={canWrite ? () => { setEditInitialField("areas"); setEditModalOpen(true); } : undefined}
          />
        </div>
      )}
      {isDrawer && activeTab === "terms" && (
        <div data-testid="lease-drawer-terms" style={bodyStyle}>
          <DrawerTermsPane
            key={selectedLease.id}
            sections={[
              { key: "parties", title: "Parties", render: () => (
                <LeaseSummaryCard lease={selectedLease} computed={computed} today={today} variant="parties" contentOnly />
              ) },
              ...["operating_expenses", "options_rights", "restrictions_restoration"].map((k) => {
                const panel = ACCORDION_PANELS.find((p) => p.key === k);
                if (!panel) return null;
                const spec = panel.fields.filter((f) => selectedLease[f] != null && selectedLease[f] !== "").length;
                return { key: k, title: panel.label.replace(/ (\w)/g, (m) => m.toLowerCase()).replace(/ & (\w)/, (m) => m.toLowerCase()),
                  count: `${spec} of ${panel.fields.length}`,
                  render: () => (
                    <TermsPaneCard contentOnly displayCcy={displayCcy} rates={rates} title={panel.label} panelKey={panel.key} fields={panel.fields} lease={selectedLease}
                      locationId={locationId} leaseId={leaseId} canWrite={canWrite} onEditOpen={handleEditOpen} />
                  ) };
              }).filter(Boolean),
            ]}
          />
        </div>
      )}
      {!isDrawer && <div style={{ ...bodyStyle, ...paneStyle(activeTab === "overview") }}>

        {/* 2×3 grid — 6 direct children */}
        <div
          data-testid="overview-grid"
          style={{
            display:             "grid",
            gridTemplateColumns: isDrawer ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)",
            gridTemplateRows:    isDrawer ? "auto" : "minmax(420px, auto) auto auto",
            gap:                 14,
            alignItems:          "stretch",
          }}
        >
          {/* R1L: Location property (photo / map / address) */}
          <div
            data-testid="overview-pane-property"
            style={{ display: "flex", flexDirection: "column", minHeight: isDrawer ? 320 : 420, order: isDrawer ? 2 : undefined }}
          >
            <LocationPropertyPane
              location={locationData}
              lease={selectedLease}
              siblingLeases={allLeases}
              asOf={today}
              onEditAreas={canWrite ? () => { setEditInitialField("areas"); setEditModalOpen(true); } : undefined}
              onOpenOffice={isDrawer ? onOpenOffice : undefined}
            />
          </div>

          {/* R1R: Key terms (8-tile grid + timeline + currency toggle) */}
          <div data-testid="overview-pane-key-terms" style={isDrawer ? { order: 0 } : undefined}>
            <KeyTermsPane
              selectedLease={selectedLease}
              computed={computed}
              displayCcy={displayCcy}
              rates={rates}
              onDisplayCcyChange={setDisplayCcy}
              today={today}
              criticalDates={criticalDates}
              decisions={decisions}
              allowances={allowances}
              officeStage={actionabilityOfficeStage}
              recordedRent={detailRecordedRent}
              compact={isDrawer}
              headerAction={isDrawer ? <>{recordStatus}{canWrite && (
                <button type="button" data-testid="key-terms-edit-lease" onClick={() => handleEditOpen(null)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: T.blue, fontSize: T.fsXs, fontWeight: T.fwBold }}>
                  Edit lease
                </button>
              )}</> : null}
            />
          </div>

          {/* R2L: AI lease summary */}
          <div data-testid="overview-pane-lease-summary" style={{ display: "flex", flexDirection: "column", order: isDrawer ? 1 : undefined }}>
            <LeaseSummaryCard
              lease={selectedLease}
              computed={computed}
              today={today}
              variant="summary"
              leases={heroLeases}
              canWrite={canWrite}
              onGenerated={applyGeneratedSummary}
              onGeneratingChange={isDrawer ? setSummaryGenerating : undefined}
            />
          </div>

          {/* R2R: Financials at a glance */}
          <div data-testid="overview-pane-financials-at-a-glance" style={{ display: isDrawer ? "none" : "flex", flexDirection: "column" }}>
            <FinancialsAtAGlanceCard
              computed={computed}
              lease={selectedLease}
              displayCcy={displayCcy}
              rates={rates}
              onTabChange={onTabChange}
            />
          </div>

          {/* R3L: Parties (lessor / lessee / contact) */}
          <div data-testid="overview-pane-parties" style={{ display: "flex", flexDirection: "column", order: isDrawer ? 3 : undefined }}>
            <LeaseSummaryCard
              lease={selectedLease}
              computed={computed}
              today={today}
              variant="parties"
            />
          </div>

          {/* R3R: Dates ahead (expiry + renewal decisions, year countdowns) */}
          <div data-testid="overview-pane-dates-ahead" style={isDrawer ? { display: "none" } : undefined}>
            <DatesAheadPane
              selectedLease={selectedLease}
              decisions={decisions}
              today={today}
              officeStage={actionabilityOfficeStage}
              recordedRent={detailRecordedRent}
            />
          </div>
        </div>

        {/* Provenance badge legend */}
        <div
          data-testid="overview-provenance-legend"
          style={{
            display:    "flex",
            alignItems: "center",
            gap:        16,
            flexWrap:   "wrap",
            marginTop:  8,
            fontSize:   T.fsXxs,
            color:      T.light,
          }}
        >
          {[
            { source: "spacebase",       label: "Spacebase" },
            { source: "computed",        label: "Calculated" },
            { source: "manual",          label: "Manual" },
            { source: "pdf_abstraction", label: "PDF" },
          ].map(({ source, label }) => (
            <span
              key={source}
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <ProvenanceBadge source={source} />
              {label}
            </span>
          ))}
        </div>

        {/* All terms expander — collapsed by default */}
        <AllTermsExpander
          selectedLease={selectedLease}
          locationId={locationId}
          leaseId={leaseId}
          canWrite={canWrite}
          onEditOpen={handleEditOpen}
        />
      </div>}

      {/* ── Financials pane ───────────────────────────────────────────────
          V5-S3 layout:
            50/50 top row: FinancialTermsCard (left) | right column stack
              right column: CostTrajectoryPanel + KeyRatiosPanel
            Full-width: RentScheduleCard
            Full-width: Concessions & allowances panel
              Allowances (drawable) subsection → ConcessionCard instances
              Abatements subsection → ConcessionCard instances
      */}
      <div style={{ ...bodyStyle, ...paneStyle(activeTab === "financials"), gap: 14 }}>

        {/* Financials pane header row: title + currency toggle */}
        <div
          data-testid="financials-pane-header"
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Financials</span>
            <PaneInfoTooltip
              label="FX conversion details"
              testId="financials-fx-info"
              interactive
              text={financialsFxTooltip}
            />
          </span>
          <CurrencyToggle
            nativeCurrency={selectedLease?.currency || "USD"}
            value={displayCcy}
            onChange={setDisplayCcy}
          />
        </div>

        {/* 6-tile headline strip */}
        <FinancialsHeadlineStrip
          lease={selectedLease}
          computed={computed}
          schedule={schedule}
          allowances={allowances}
          displayCcy={displayCcy}
          rates={rates}
          columns={isDrawer ? 4 : null}
          extraTile={isDrawer ? (
            <RemainingObligationTile lease={selectedLease} schedule={schedule} displayCcy={displayCcy} rates={rates} today={today} />
          ) : null}
        />
        {isDrawer && <LifetimeCoverageNote lease={selectedLease} schedule={schedule} />}

        {/* 2/3 + 1/3 hero row: CostTrajectoryPanel (left, hero) | KeyRatiosPanel (right) */}
        <div
          data-testid="financials-top-row"
          style={{
            display:             "grid",
            gridTemplateColumns: isDrawer ? "minmax(0, 1fr)" : "2fr 1fr",
            gap:                 14,
            alignItems:          "stretch",
          }}
        >
          {/* Left 2/3: Cost trajectory hero chart */}
          <div data-testid="financials-left-col">
            {isDrawer ? (
              <LeaseDrawerTrajectory schedule={schedule} nativeCcy={selectedLease.currency || "USD"} displayCcy={displayCcy} rates={rates} today={today} />
            ) : <CostTrajectoryPanel
              schedule={schedule}
              currentYear={currentYear}
              nativeCcy={selectedLease.currency || "USD"}
              displayCcy={displayCcy}
              rates={rates}
            />}
          </div>

          {/* Right 1/3: Key ratios */}
          <div data-testid="financials-right-col" style={{ height: "100%" }}>
            <KeyRatiosPanel
              drawer={isDrawer}
              schedule={schedule}
              allowances={allowances}
              nativeCcy={selectedLease.currency || "USD"}
              displayCcy={displayCcy}
              rates={rates}
              freeRentMonths={selectedLease.freeRentMonths}
            />
          </div>
        </div>

        {/* Financial terms — full-width row below the hero chart */}
        <div data-testid="financials-terms-row">
          <FinancialTermsCard
            drawer={isDrawer}
            lease={selectedLease}
            computed={computed}
            displayCcy={displayCcy}
            rates={rates}
            schedulePeriod={schedule?.period}
          />
        </div>

        {linkedHeadLease && (
          <div
            data-testid="sublease-head-rollup-note"
            style={{ padding: "10px 13px", border: `1px solid ${T.border}`, borderRadius: 9, color: T.mid, fontSize: T.fsSm }}
          >
            Sublease of{" "}
            <Link to={buildLeaseUrl(linkedHeadLease.id)} style={{ color: T.blue, textDecoration: "none" }}>
              {linkedHeadLease.id}
            </Link>
            {" "}— income rolls up there.
          </div>
        )}

        {isPinnedLease && hasLinkedSubOuts && selectedLease.subleaseRecoveryEnabled === true && selectedLease.type === "direct_lease" && (
          <SubleaseRecoveryPanel
            displayCcy={isDrawer ? displayCcy : undefined}
            lease={selectedLease?.id === primaryLease?.id ? { ...selectedLease, computed } : selectedLease}
            records={recoveryRecords}
            loading={recoveryLoading}
            error={recoveryError}
            rates={rates}
          />
        )}

        {/* Rent schedule — full width below the 50/50 row */}
        {isDrawer ? (
          <LeaseDrawerRentSchedule lease={selectedLease} schedule={schedule} displayCcy={displayCcy} rates={rates} today={today} />
        ) : <RentScheduleCard
          areas={selectedLease.areas}
          lease={selectedLease}
          schedule={schedule}
          currentYear={currentYear}
          currency={selectedLease.currency || "USD"}
          displayCcy={displayCcy}
          rates={rates}
          open={!!accordionOpen["rent_schedule"]}
          onToggle={() => toggleAccordion("rent_schedule")}
          isDerived={!!computed?.isDerived}
          leaseStartYear={
            selectedLease.startDate
              ? new Date(selectedLease.startDate).getFullYear()
              : undefined
          }
          leaseEndYear={
            selectedLease.endDate
              ? new Date(selectedLease.endDate).getFullYear()
              : undefined
          }
          allowances={allowances}
          criticalDates={criticalDates}
        />}

        {/* Concessions & allowances — split into two labelled subsections */}
        {allowances.length > 0 && (() => {
          const drawable   = allowances.filter((a) => a.type !== "abatement");
          const abatements = allowances.filter((a) => a.type === "abatement");
          return (
            <section
              data-testid="concessions-panel"
              style={{ ...PANEL_CARD, padding: "13px 16px" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <PaneHeading style={{ marginBottom: 0 }}>Concessions &amp; allowances</PaneHeading>
                <span
                  style={{
                    fontSize: T.fsXxs,
                    fontWeight: T.fwSemibold,
                    background: T.blueSoft,
                    color: T.blue,
                    borderRadius: 999,
                    padding: "1px 7px",
                  }}
                >
                  {allowances.length}
                </span>
              </div>

              {drawable.length > 0 && (
                <>
                  <ConcessionSubheading>Allowances (drawable)</ConcessionSubheading>
                  <div
                    data-testid="concessions-drawable-list"
                    style={{ display: "flex", flexDirection: "column", gap: 10 }}
                  >
                    {drawable.map((alw) => (
                      <ConcessionCard
                        key={alw.id}
                        allowance={alw}
                        locationId={locationId}
                        leaseId={leaseId}
                        canWrite={canWrite}
                        onDrawAdded={refreshAllowances}
                        displayCcy={displayCcy}
                        rates={rates}
                      />
                    ))}
                  </div>
                </>
              )}

              {abatements.length > 0 && (
                <>
                  <ConcessionSubheading>
                    Abatements (auto-applied, net into rent schedule)
                  </ConcessionSubheading>
                  <div
                    data-testid="concessions-abatements-list"
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    {abatements.map((alw) => (
                      <AbatementEntry
                        key={alw.id}
                        allowance={alw}
                        displayCcy={displayCcy}
                        rates={rates}
                      />
                    ))}
                  </div>
                </>
              )}
            </section>
          );
        })()}
      </div>

      {/* ── Dates pane ────────────────────────────────────────────────────
          LEASE-DATES-REDESIGN: hero + notice gauge + SVG timeline +
          merged upcoming list + history list. No new server calls;
          lease and abatementAllowances are already fetched above.
      */}
      <div style={{ ...bodyStyle, ...paneStyle(activeTab === "dates") }}>
        <LeaseDatesPane
          readState={isDrawer && readErrors.some(e => e.label === "Critical dates") ? "failed" : "ready"}
          criticalDates={criticalDates}
          canWrite={canWrite}
          locationId={locationId}
          leaseId={leaseId}
          lease={selectedLease}
          abatementAllowances={allowances.filter((a) => a.type === "abatement")}
          compact={isDrawer}
        />
      </div>

      {/* ── Documents pane ────────────────────────────────────────────────
          LeaseDocumentsTab — read-only register of uploaded documents.
          "+ Upload" is a Phase-2 affordance; no write path wired yet.
      */}
      <div style={{ ...bodyStyle, ...paneStyle(activeTab === "documents") }}>
        <LeaseDocumentsTab documents={documents} />
      </div>

      {/* ── Intel pane ────────────────────────────────────────────────────
          IntelTab — has its own internal fetch; keep-alive via display:none
          so notes are not re-fetched on every tab switch.
      */}
      <div style={paneStyle(activeTab === "intel")}>
        <IntelTab
          locationId={locationId}
          locationText={selectedLease.locationAlias || locationId}
          currentRole={currentRole}
        />
      </div>

      {/* EditLeaseModal — opened by "+ add" buttons in the Terms pane cards.
          Mounted at body level so it has access to refreshPrimaryLease. */}
      {editModalOpen && selectedLease && (
        <EditLeaseModal
          isOpen={editModalOpen}
          onClose={() => { setEditModalOpen(false); setEditInitialField(null); }}
          lease={selectedLease}
          canWrite={canWrite}
          isAdmin={currentRole === "admin"}
          initialField={editInitialField}
          onSaved={() => {
            setEditModalOpen(false);
            setEditInitialField(null);
            setAreaRefreshKey((key) => key + 1);
            onLeaseSaved?.();
          }}
          onDeleted={() => {
            setEditModalOpen(false);
            setEditInitialField(null);
            refreshPrimaryLease();
            onLeaseSaved?.({ deleted: true });
          }}
        />
      )}

    </div>
  );
}
